'use client';

import DetailContent from '@/components/sections/builder/elements/body/blog-home/post-list/detail-content';
import {
  BlogHomeElementDataType,
  BlogHomeListElementDataType,
} from '@/components/sections/builder/elements/body/blog-home/type';
import InputSelect from '@/components/sections/builder/settings/input-select';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import ModalSelectBlog from '@/components/sections/builder/settings/modal-select-blog';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { SORTINGBLOGPOST } from '@/constant/dummyData/dummy-filter-sorting';

import { useBlogsList } from '@/hooks/api/storefront/blogs';
import useModal from '@/hooks/useModal';
import { cn } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { AdminBlogListResponse } from '@/types/http';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronDown, Columns3, ExternalLinkIcon, RefreshCcwIcon, Trash2Icon } from 'lucide-react';
import React from 'react';
import { shallow } from 'zustand/shallow';

const defaultData: BlogHomeListElementDataType = {
  name: 'Blog Post',
  source: '',
  sorting: 'created-descending',
  limit: 4,
  columnsMobile: 2,
  showImage: true,
  showDate: true,
  showAuthor: true,
  showCategory: true,
  showContent: true,
  showShadow: true,
  rounded: 10,
  gap: 1,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as BlogHomeListElementDataType;
  const parentData = element.parentData as BlogHomeElementDataType;
  const autoplay = parentData.autoplay ? true : false;

  const queryParams = SORTINGBLOGPOST[data.sorting as keyof typeof SORTINGBLOGPOST] || {};

  const { data: blogs } = useBlogsList(
    data.source,
    {
      ...queryParams,
    },
    {
      enabled: !!data.source,
    }
  );

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const delay = parentData.autoplay ? (parentData?.changeSlidesEvery || 5) * 1000 : 0;
  const plugin = React.useRef(
    Autoplay({
      delay: delay,
      playOnInit: autoplay,
    })
  );

  return (
    <>
      <div
        className={cn(
          'theme_layout_space_grid_horizontal theme_layout_space_grid_vertical place-content-center',
          'p-3',
          'lg:pb-[--p-bottom] lg:pl-[--p-left] lg:pr-[--p-right] lg:pt-[--p-top]',
          !parentData.enableCarousel ? 'grid' : 'hidden',
          `grid-cols-${data.columnsMobile} md:grid-cols-${data.limit}`
        )}
        style={
          {
            gap: `${data.gap}px`,
            '--p-top': `${data.padding?.top || 0}px`,
            '--p-bottom': `${data.padding?.bottom || 0}px`,
            '--p-left': `${data.padding?.left || 0}px`,
            '--p-right': `${data.padding?.right || 0}px`,
          } as React.CSSProperties
        }
      >
        {blogs?.map((blog, idx) => (
          <DetailContent
            key={idx}
            index={idx}
            data={data}
            blog={blog}
            elementId={element.id}
            theme={builder.theme}
          />
        ))}
      </div>
      <div
        className={cn(
          'blog-post-list-padding theme_layout_space_grid_horizontal theme_layout_space_grid_vertical place-content-center',
          !parentData.enableCarousel ? 'hidden' : 'block',
          `grid-cols-${data.columnsMobile} md:grid-cols-${data.limit}`
        )}
      >
        <Carousel
          className='max-w-full'
          plugins={autoplay ? [plugin.current] : []}
          opts={{ loop: true }}
        >
          <CarouselContent>
            {blogs?.map((blog, idx) => (
              <CarouselItem className='carousel-item-column' key={idx}>
                <DetailContent
                  index={idx}
                  data={data}
                  blog={blog}
                  elementId={element.id}
                  theme={builder.theme}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className='left-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
          <CarouselNext className='right-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
        </Carousel>
      </div>
      <style jsx global>{`
        @media (min-width: 768px) {
          .carousel-item-column {
            flex-basis: ${100 / data.limit}% !important;
          }
        }
        .carousel-item-column {
          flex-basis: ${100 / (data.columnsMobile || 1)}%;
        }
      `}</style>
      <style jsx>
        {`
          .theme_blog_card {
            background-color: ${parentData.colorSchemeBlogHome?.background};
            color: ${parentData.colorSchemeBlogHome?.text};
          }
          .post-gap {
            margin-right: ${data.gap || 0}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Columns3;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
      setElementData: state.setElementData,
    }),
    shallow
  );

  const data = element.data as BlogHomeListElementDataType;

  const test = element;

  const selectBlogsModal = useModal<Partial<AdminBlogListResponse>>();

  if (data.source === '') {
    return (
      <>
        <Button
          type='button'
          variant='outline'
          className='flex w-full gap-x-2 bg-white'
          onClick={() => {
            selectBlogsModal.open();
          }}
        >
          <span>Select Blogs</span>
        </Button>
        <ModalSelectBlog
          openModal={selectBlogsModal.isShow}
          setOpenModal={(e) => !e && selectBlogsModal.setIsShow(e)}
          element={element}
        />
      </>
    );
  }

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Body</h1>
      <div className='mb-3 flex flex-col gap-2'>
        <h4>Source</h4>
        <div className='rounded-md bg-gray-50 py-4'>
          <div className='mb-4 flex gap-x-2 border-b border-white px-4 pb-3'>
            <div>{data.source}</div>
            <ExternalLinkIcon className='h-4 w-4' />
          </div>
          <div className='px-4'>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  className='flex w-full gap-x-2 bg-white text-sm font-medium'
                >
                  <span>Change</span>
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='w-full'>
                <DropdownMenuItem
                  onClick={() => {
                    selectBlogsModal.open();
                  }}
                >
                  <RefreshCcwIcon className='mr-2 h-4 w-4' />
                  <div>Change Blog</div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    builder.setChildData(element.parentId as string, element.id, {
                      source: '',
                    });
                  }}
                >
                  <Trash2Icon className='mr-2 h-4 w-4' />
                  <div>Remove Blog</div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Sorting</h4>
        <InputSelect
          defaultValue={data.sorting || defaultData.sorting}
          options={[
            { label: 'Alphabetically, A-Z', value: 'title-ascending' },
            { label: 'Alphabetically, Z-A', value: 'title-descending' },
            { label: 'Newest first', value: 'created-descending' },
            { label: 'Oldest first', value: 'created-ascending' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, { sorting: v })
          }
        />
      </div>

      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on desktop</h4>
        <InputSlider
          value={data.limit || 1}
          max={5}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              limit: val,
            })
          }
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on mobile</h4>
        <InputSlider
          value={data.columnsMobile ?? 2}
          max={2}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              columnsMobile: val,
            })
          }
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showImage'>Show Image</label>
        <Switch
          id='showImage'
          aria-label='Select show image'
          className='ml-auto'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showImage: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showAuthor'>Show Author</label>
        <Switch
          id='showAuthor'
          aria-label='Select show author'
          className='ml-auto'
          checked={data.showAuthor}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showAuthor: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showDate'>Show Date</label>
        <Switch
          id='showDate'
          aria-label='Select show date'
          className='ml-auto'
          checked={data.showDate}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showDate: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showContent'>Show Content</label>
        <Switch
          id='showContent'
          aria-label='Select show content'
          className='ml-auto'
          checked={data.showContent}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showContent: !!checked,
            });
          }}
          aria-readonly
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-4 mt-4 text-sm font-bold'>Style</h1>

      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Gap Post</h4>
        <InputSlider
          value={data.gap || 0}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              gap: val,
            })
          }
        />
      </div>

      <PaddingSetting
        value={data.padding}
        hideLeft={true}
        hideRight={true}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />

      <ModalSelectBlog
        openModal={selectBlogsModal.isShow}
        setOpenModal={(e) => !e && selectBlogsModal.setIsShow(e)}
        element={test}
      />
    </div>
  );
}

const BlogHomeList = {
  type: 'blog_home_list',
  group: 'blog_home',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogHomeList;
